import { REPORTS_PATH } from '../constants/reports';
import {
  AdminPanelSettings,
  AssessmentRounded,
  AssignmentIndRounded,
  AssignmentRounded,
  CallOutlined,
  Casino,
  EmailOutlined,
  Flare,
  ManageAccountsRounded,
  MonetizationOn,
  NoteAlt,
  PersonOutlined,
  Settings,
  SportsVolleyball,
  Stars,
  Store,
  Payments,
  Handshake,
} from '@mui/icons-material';
import { SvgIconProps } from '@mui/material';

import { EditTabRecord, EntityEditTabType, Permission } from '../@types';
import { CasinoConfigFlag, MarketplaceSystemFlag } from '../@types/api';
import ConditionsTab from '../components/organisms/ConditionsTab';
import DisplayTypeTab from '../components/organisms/DisplayTypeTab';
import MatchCombiningTab from '../components/organisms/MatchCombiningTab';
import SportEventPartsTab from '../components/organisms/SportEventPartsTab';
import MarketCombiningTab from '../components/organisms/MarketCombiningTab';
import MarketplaceConfigTab from '../components/organisms/MarketplaceConfigTab';
import MarketplaceSportsbookConfigTab from '../components/organisms/MarketplaceSportsbookConfigTab';
import CasinoConfigTab from '../components/organisms/CasinoConfigTab';
import TranslationTab from '../components/organisms/TranslationTab';

export const CASINO_PATH = '/casino';

export type NavigationObjectType = {
  name: string;
  icon: React.ComponentType<SvgIconProps>;
  to: string;
  children?: NavigationObjectType[];
  permission?: Permission;
  requireSportsbook?: boolean;
  featureFlag?: string;
  standaloneCasinoConfigFlag?: CasinoConfigFlag;
  marketplaceSystemFlag?: MarketplaceSystemFlag;
  standaloneCasinoSystemFlag?: MarketplaceSystemFlag;
};

export type Config = {
  Products: NavigationObjectType[];
  Features: NavigationObjectType[];
  Systems: NavigationObjectType[];
  Reports: NavigationObjectType[];
  Settings: NavigationObjectType[];
};

export const NAVIGATION_ITEMS_LIST: Config = {
  Products: [
    {
      name: 'Sports Betting',
      icon: SportsVolleyball,
      to: '/sports-betting',
      permission: 'accessSportsBetting',
      requireSportsbook: true,
      children: [
        {
          name: 'Settings',
          icon: Settings,
          to: '/settings',
          permission: 'manageBettingSettings',
        },
        {
          name: 'Offer management',
          icon: NoteAlt,
          to: '/offer-management',
          permission: 'manageOffer',
        },
      ],
    },
    {
      name: 'Casino',
      icon: Casino,
      to: CASINO_PATH,
      permission: 'accessCasino',
    },
  ],
  Features: [
    {
      name: 'Bonus',
      icon: Stars,
      to: '/bonus',
      permission: 'manageBonuses',
    },
    {
      name: 'Jackpot',
      icon: Flare,
      to: '/jackpot',
      permission: 'manageJackpots',
      standaloneCasinoConfigFlag: 'jackpotEnabled',
    },
    {
      name: 'Cash out',
      icon: MonetizationOn,
      to: '/cash-out',
      permission: 'manageCashOut',
      requireSportsbook: true,
    },
  ],
  Systems: [
    {
      name: 'CMS',
      icon: AssignmentRounded,
      to: '/cms',
      permission: 'accessCMS',
    },
    {
      name: 'CRM',
      icon: AssignmentIndRounded,
      to: '/crm',
      permission: 'accessCRM',
    },
    {
      name: 'Affiliate',
      icon: Handshake,
      to: '/affiliate',
      permission: 'accessCampaigns',
      marketplaceSystemFlag: 'affiliate',
    },
    {
      name: 'User Management',
      icon: ManageAccountsRounded,
      to: '/user-management',
      permission: 'accessUserManagement',
    },
  ],
  Reports: [
    {
      name: 'Betting reports',
      icon: AssessmentRounded,
      to: REPORTS_PATH,
      permission: 'accessProductReports',
    },
  ],
  Settings: [
    {
      name: 'Payments',
      icon: Payments,
      to: '/payments',
      permission: 'accessPaymentSettings',
      featureFlag: 'paymentSettings',
    },
    {
      name: 'Marketplace',
      icon: Store,
      to: '/marketplace',
      permission: 'accessMarketplace',
    },
    {
      name: 'Super Admin',
      icon: AdminPanelSettings,
      to: '/super-admin',
      permission: 'defaultSuperAdminRole',
    },
  ],
};

const EDIT_TAB_CONFIG: Record<string, EditTabRecord> = {
  conditions: {
    label: 'Conditions',
    component: ConditionsTab,
    permission: 'accessConditions',
  },
  displayType: {
    label: 'Display type',
    component: DisplayTypeTab,
  },
  matchCombining: {
    label: 'Match combining',
    component: MatchCombiningTab,
  },
  eventParts: {
    label: 'Event parts',
    component: SportEventPartsTab,
  },
  marketCombining: {
    label: 'Combining',
    component: MarketCombiningTab,
    permission: 'accessMarketCombining',
  },
  marketplaceConfig: {
    label: 'Marketplace',
    component: MarketplaceConfigTab,
  },
  marketplaceSportsbook: {
    label: 'Sportsbook',
    component: MarketplaceSportsbookConfigTab,
  },
  casinoConfig: {
    label: 'Casino',
    component: CasinoConfigTab,
  },
  translation: {
    label: 'Translation',
    component: TranslationTab,
  },
};

export const ENTITY_EDIT_TABS_MAP = {
  sports: [EDIT_TAB_CONFIG.conditions, EDIT_TAB_CONFIG.eventParts, EDIT_TAB_CONFIG.translation],
  competitions: [EDIT_TAB_CONFIG.conditions, EDIT_TAB_CONFIG.translation],
  tournaments: [EDIT_TAB_CONFIG.conditions, EDIT_TAB_CONFIG.translation],
  participants: [],
  players: [],
  'market-types': [EDIT_TAB_CONFIG.conditions, EDIT_TAB_CONFIG.marketCombining, EDIT_TAB_CONFIG.translation],
  'outcome-types': [EDIT_TAB_CONFIG.translation],
  'market-type-groups': [EDIT_TAB_CONFIG.translation],
  'market-type-group-categories': [EDIT_TAB_CONFIG.translation],
  'condition-types': [EDIT_TAB_CONFIG.translation],
  events: [EDIT_TAB_CONFIG.matchCombining, EDIT_TAB_CONFIG.conditions],
  outrights: [EDIT_TAB_CONFIG.matchCombining, EDIT_TAB_CONFIG.conditions],
  liveEvents: [],
  punters: [EDIT_TAB_CONFIG.conditions],
  marketplace: [EDIT_TAB_CONFIG.marketplaceConfig, EDIT_TAB_CONFIG.marketplaceSportsbook, EDIT_TAB_CONFIG.casinoConfig],
};

export type TranslatableField = 'name' | 'shortName' | 'description' | 'restrictionMsgClient' | 'verificationMsgClient';

export type TranslationField = {
  field: TranslatableField;
  label: string;
  multiline?: boolean;
};

export const TRANSLATION_FIELD_CONFIG: Partial<Record<EntityEditTabType, TranslationField[]>> = {
  'market-types': [
    { field: 'shortName', label: 'Short Name' },
    { field: 'description', label: 'Description', multiline: true },
  ],
  'market-type-groups': [{ field: 'name', label: 'Name' }],
  'market-type-group-categories': [{ field: 'name', label: 'Name' }],
  'condition-types': [
    { field: 'verificationMsgClient', label: 'Verification message client', multiline: true },
    { field: 'restrictionMsgClient', label: 'Restriction message client', multiline: true },
  ],
};

export const DEFAULT_TRANSLATION_FIELDS: TranslationField[] = [{ field: 'shortName', label: 'Short Name' }];

export const CRM_TASK_TYPE_CONFIG = {
  CALL: {
    color: '#55CDA9',
    Icon: CallOutlined,
  },
  MESSAGE: {
    color: '#5BACE7',
    Icon: EmailOutlined,
  },
  APPOINTMENT: {
    color: '#F7B64E',
    Icon: PersonOutlined,
  },
} as const;
